import React, { useState, useMemo } from "react";
import { Download, Search } from "react-feather";
import SingleSubmissionModal from "../../../../components/Modals/SingleSubmissionModal";
import SubmissionResultIndicator from "../../../../components/Table/SubmissionResultIndicator";
import ActionButtonForTables from "../../../../components/Table/ActionButtonForTables";
import CustomDataTableIcons from "../../../../components/Table/CustomDataTableIcons";
import DateTooltip from "../../../../components/Table/DateTooltip";
import TableTitle from "../../../../components/Table/TableTitle";
import { makeDefaultTableOptions } from "../../../../storage/helpers";
import { CustomDatatableV2 } from "../../styles";
import SubmissionChipExtraCreditTooltip from "src/components/UI/SubmissionChipExtraCreditTooltip";
import SubmissionChipAssignedTooltip from "src/components/UI/SubmissionChipAssignedTooltip";
import DisplayScore from "../../../../components/UI/DisplayScore";

import { ENGAGEMENT_TYPES } from "../../../../storage/constants";

const options = makeDefaultTableOptions("submissions-last-14");
options.sortOrder = {
  name: "date",
  direction: "desc",
};

const NewSubmissionsTable = ({
  newSubmissions,
  submissionsType,
  displayTeamMemberLocation = false,
}) => {
  const [submissionInfo, setSubmissionInfo] = useState(null);

  const cleanSubmissions = useMemo(() => {
    if (submissionsType === ENGAGEMENT_TYPES.ALL) return newSubmissions;

    if (submissionsType === ENGAGEMENT_TYPES.ASSIGNED) {
      return newSubmissions.filter((s) => s.assigned);
    }

    return newSubmissions.filter((s) => !s.assigned);
  }, [submissionsType]);

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: false,
        display: false,
        download: false,
      },
    },
    {
      name: "date",
      label: "Date",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => <DateTooltip date={value} />,
      },
    },
    {
      name: "name",
      label: "Member Name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "memberLocation",
      label: "Member Location",
      options: {
        filter: true,
        sort: true,
        display: displayTeamMemberLocation,
      },
    },
    {
      name: "quiz",
      label: "Module Name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "subType",
      label: "Type",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (val) => {
          if (val === "assigned") {
            return <SubmissionChipAssignedTooltip />;
          }
          return <SubmissionChipExtraCreditTooltip />;
        },
      },
    },
    {
      name: "score",
      label: "Score",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (val) => <DisplayScore value={val} />,
      },
    },
    {
      name: "passed",
      label: "Passed",
      options: {
        filter: true,
        sort: true,
        display: true,
        customBodyRender: (value) => (
          <SubmissionResultIndicator value={value} />
        ),
      },
    },
    {
      name: "",
      label: "",
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => (
          <ActionButtonForTables
            title="view"
            Icon={Search}
            handleClick={() => setSubmissionInfo({ id: tableMeta.rowData[0] })}
          />
        ),
      },
    },
  ];

  const tableData = cleanSubmissions.map((submission) => ({
    id: submission.id,
    name: `${submission.first_name} ${submission.last_name}`,
    memberLocation: submission?.active_team_members?.[0]?.location_name || null,
    date: new Date(submission.created).toISOString(),
    quiz: submission.quiz_name,
    subType: submission.assigned ? "assigned" : "ec",
    score: `${submission.score}/${submission.questions}`,
    passed: submission.passed ? "Yes" : "No",
  }));

  return (
    <>
      <CustomDatatableV2
        title={
          <TableTitle
            Icon={Download}
            title="Recent Submissions"
            submissionType={submissionsType}
          />
        }
        options={options}
        columns={columns}
        data={tableData}
        components={{
          icons: CustomDataTableIcons,
        }}
      />
      <SingleSubmissionModal
        submissionInfo={submissionInfo}
        setSubmissionInfo={setSubmissionInfo}
      />
    </>
  );
};

export default NewSubmissionsTable;
